import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import {
    DataEventProps,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src";

import {
    GlobalNavProductRecommendationsExperiment,
    useGlobalNavProductRecommendationsExperiment
} from "../global-nav-product-recommendations-experiment";

import { Divider } from "./icons/divider";
import { MobileNavCard, MobileNavCardProps } from "./mobile-nav-card";

const topLinksContainerCss = css({
    paddingBlockEnd: "24px"
});

const labelCss = css({
    color: "#101214",
    textTransform: "uppercase",
    fontFamily: "Charlie Text",
    fontSize: "13px",
    fontWeight: "500"
});

const pageLabelCss = css({
    paddingBlock: token("space.150"),
    lineHeight: "1.667"
});

const sectionLabelCss = css({
    paddingBlock: "28px 8px",
    lineHeight: "1.528"
});

const bottomContainerCss = css({
    backgroundColor: "#F4F5F7",
    margin: "0 -24px -40px -24px",
    padding: "0 24px 40px 24px"
});

const linkContainerCss = css({
    paddingBlock: token("space.200"),
    fontFamily: "Charlie Text",
    fontSize: "14px",
    fontWeight: "700",
    lineHeight: "1.428"
});

export type SubnavLinkProps = {
    key: string;
    href: string;
    text: string;
    productKey?: ProductKeyTypes;
};

const SubnavLink = ({
    href,
    text,
    productKey = "home"
}: Omit<SubnavLinkProps, "key">) => {
    return (
        <div css={linkContainerCss}>
            <Link
                type="anchor"
                href={href}
                text={text}
                ariaLabel={text}
                customLinkColor="#101214"
                dataEventProps={
                    {
                        "data-product-key": productKey,
                        "data-event-component": "linkButton",
                        "data-event-container": "globalNavWacDropdownContainer",
                        "data-event": "clicked"
                    } as DataEventProps
                }
            />
        </div>
    );
};

export type LinkSection = {
    key: string;
    name: string;
    links: SubnavLinkProps[];
};

type MobileNavMenuPageProps = {
    pageLabel: string;
    cards: MobileNavCardProps[];
    linksSections?: LinkSection[];
    productKey?: ProductKeyTypes;
};

export const MobileNavMenuPage = ({
    pageLabel,
    cards,
    linksSections,
    productKey = "home"
}: MobileNavMenuPageProps) => {
    const {
        shouldShowGlobalNavProductRecommendations,
        mobileNavPageLabelFeaturedProducts,
        ...globalNavProductRecommendationsProps
    } = useGlobalNavProductRecommendationsExperiment({ isMobile: true });

    return (
        <>
            <div css={topLinksContainerCss}>
                <div css={[labelCss, pageLabelCss]}>
                    <span>{pageLabel}</span>
                </div>
                <div>
                    {pageLabel === mobileNavPageLabelFeaturedProducts &&
                    shouldShowGlobalNavProductRecommendations ? (
                        <GlobalNavProductRecommendationsExperiment
                            isMobile
                            {...globalNavProductRecommendationsProps}
                        />
                    ) : (
                        cards.map(({ key, ...rest }) => (
                            <MobileNavCard
                                key={key}
                                productKey={productKey}
                                {...rest}
                            />
                        ))
                    )}
                </div>
            </div>
            {linksSections && (
                <div css={bottomContainerCss}>
                    {linksSections?.map(({ key, name, links }, numSection) => (
                        <div key={`section-${key}`}>
                            {numSection !== 0 && <Divider />}
                            <div css={[labelCss, sectionLabelCss]}>
                                <span>{name}</span>
                            </div>
                            {links.map(({ key, ...rest }) => (
                                <SubnavLink
                                    key={key}
                                    productKey={productKey}
                                    {...rest}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};
